import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, TrendingUp, Users, Globe, ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const CaseStudiesPage: React.FC = () => {
  useSEO('case-studies');
  const { isBannerVisible, bannerHeight } = useBanner();

  const caseStudy = {
    clientName: "Holistic Manual Physical Therapy",
    website: "https://holisticmanualpt.com/",
    logo: "https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/7a79cece-917d-48cd-db56-30d20e671900/public",
    industry: "Healthcare & Wellness",
    projectType: "Professional Website Development",
    launchDate: "2024",
    description: "A complete digital launch for a new manual physical therapy practice, establishing their first online presence with patient education, service showcase, and professional branding from the ground up.",
    challenges: [
      "New business with no existing online presence",
      "Complex service offerings needed clear presentation", 
      "Required professional healthcare industry design standards",
      "Need to establish credibility and trust for new practice"
    ],
    solutions: [
      "Complete brand identity and website development from scratch",
      "Modern, responsive website design with healthcare focus",
      "Clear service categorization and treatment explanations",
      "Patient-friendly interface with easy appointment access",
      "Professional branding aligned with healthcare standards",
      "SEO optimization for local healthcare searches"
    ],
    results: [
      {
        metric: "Online Presence",
        improvement: "Established from Zero",
        description: "Created complete digital presence for new business launch"
      },
      {
        metric: "User Engagement", 
        improvement: "Greatly Improved",
        description: "Enhanced patient interaction and site navigation"
      },
      {
        metric: "Professional Image",
        improvement: "Complete Transformation",
        description: "Elevated brand perception and healthcare credibility"
      },
      {
        metric: "Patient Accessibility",
        improvement: "Enhanced",
        description: "Streamlined information access and service discovery"
      }
    ],
    testimonial: {
      quote: "Cognitive Craft Hub helped us launch our practice with a professional digital presence from day one. Our website perfectly represents our healthcare services and has been essential in establishing credibility and attracting new patients to our new practice.",
      author: "Holistic Manual Physical Therapy Team"
    },
  };

  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 py-4 px-2 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent leading-loose overflow-visible min-h-fit"
            style={{ lineHeight: '1.75', minHeight: 'auto', overflow: 'visible' }}
          >
            Case Studies
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-7xl mx-auto leading-relaxed">
            Discover how we've helped businesses transform their digital presence through detailed project analysis
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Client Showcase */}
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-20 h-20 rounded-xl flex items-center justify-center p-2">
                    <img 
                      src={caseStudy.logo}
                      alt={`${caseStudy.clientName} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{caseStudy.clientName}</h2>
                    <p className="text-blue-600 dark:text-blue-400 font-medium">{caseStudy.industry}</p>
                  </div>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
                  {caseStudy.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Project Type</h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{caseStudy.projectType}</p>
                </div>

                <a 
                  href={caseStudy.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Visit Website
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>

              <div className="bg-gradient-to-br from-blue-600/10 to-blue-700/10 rounded-2xl p-8 text-center">
                <Globe className="w-24 h-24 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Healthcare Excellence</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Professional digital presence for healthcare providers
                </p>
              </div>
            </div>
          </section>

          {/* Results Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">Project Results</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {caseStudy.results.map((result, index) => (
                <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{result.metric}</h3>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">{result.improvement}</div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{result.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Challenges & Solutions */}
          <section className="grid lg:grid-cols-2 gap-8 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Challenges</h3>
              <ul className="space-y-4">
                {caseStudy.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-blue-300 dark:bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700 dark:text-gray-300">{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Solutions</h3>
              <ul className="space-y-4">
                {caseStudy.solutions.map((solution, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 dark:bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700 dark:text-gray-300">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Testimonial */}
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16 text-center">
            <div className="max-w-3xl mx-auto">
              <Users className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-6" />
              <blockquote className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed mb-6 italic">
                "{caseStudy.testimonial.quote}"
              </blockquote>
              <cite className="text-gray-600 dark:text-gray-400 font-medium">
                — {caseStudy.testimonial.author}
              </cite>
            </div>
          </section>


          {/* Call to Action */}
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Create Your Case Study?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Let's discuss how we can help transform your digital presence and develop a detailed analysis of your project results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Start Your Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                View Our Services
              </Link>
            </div>
          </section>

          {/* Results Disclaimer */}
          <section className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Important Notice About Results</h3>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              <p>
                <strong>Case Study Disclaimer:</strong> The results presented in this case study are specific to this 
                particular client and project. Individual results may vary based on numerous factors including industry, 
                market conditions, implementation approach, and client-specific circumstances.
              </p>
              <p>
                <strong>No Guarantee of Results:</strong> While we strive to deliver exceptional outcomes for all clients, 
                we cannot guarantee specific results, performance improvements, or business outcomes. Each project is unique 
                and success depends on various factors beyond our direct control.
              </p>
              <p>
                <strong>Testimonial Authenticity:</strong> Client testimonials represent genuine feedback from real clients. 
                However, individual experiences may vary and should not be considered as typical or guaranteed results for future projects.
              </p>
              <p>
                <strong>Professional Services:</strong> Results are achieved through professional service delivery and may 
                require ongoing collaboration, maintenance, and optimization to sustain performance improvements over time.
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};